import { useEffect, useRef, useState } from 'react';
import { Folder, FolderOpen, ChevronRight, ChevronDown, MoreVertical } from '../../icons';
import { listFolderChildren, foldersOf, type NodePublic } from '../../api/workspace';
import { tWorkspace } from '../../lib/workspace-i18n';
import { hasWorkspaceNodes, readWorkspaceNodes, hasExternalFiles, readExternalFiles } from '../../lib/workspace-dnd';

interface FolderTreeProps {
  /**
   * 폴더 줄 끝 「…」 메뉴 (2026-10-15 사용자 지시 — 「내 파일 목록 오른쪽 끝에 … 을 만들어 이름 변경 등」).
   * 주면 줄마다 「…」 단추가 서고, 누르면 부르는 쪽이 본문 목록과 **같은** 줄 메뉴(`WorkspaceRowMenu`)를 띄운다.
   * 안 주면 종전과 한 픽셀도 다르지 않다.
   */
  onFolderMenu?: (folder: NodePublic, anchor: HTMLElement) => void;
  activeFolderId: string | null;
  onSelect: (folder: NodePublic | null) => void;
  /**
   * 다시 읽으라는 신호 (2026-08-14, 기본 0).
   *
   * 예전에는 mount 시 1회만 조회해서, 폴더를 만들거나 지워도 트리가 그대로였다
   * ("만들었는데 반영이 안 된다"). 값이 바뀌면 루트를 다시 읽고 하위 캐시를 버린다.
   */
  version?: number;
  /**
   * 다시 읽을 때 **펼쳐 보일** 폴더 id 들 — 지금 있는 폴더까지의 경로 (2026-10-27 사용자 지시).
   * 「폴더를 만들면 내 파일 목록에 실시간으로 안 생긴다 · 새로고침해야 한다」 — 만든 폴더는 지금 있는
   * 폴더 **안**에 생기는데, 그 폴더가 트리에서 접혀 있으면 다시 읽어도 안 보였다.
   * `version` 이 바뀔 때만 쓴다(펼침을 늘 강요하지 않는다). 안 주면 종전과 같다.
   */
  revealIds?: readonly string[];
  /**
   * 끌어다 이 폴더에 놓기 (2026-08-18) — 목록에서 끌어온 것을 트리의 폴더로 옮긴다.
   * `folderId=null` 은 루트('워크스페이스'). 안 주면 트리는 지금까지처럼 받지 않는다.
   */
  onDropMove?: (nodeIds: readonly string[], folderId: string | null) => void;
  /** 바깥에서 끌어온 파일을 이 폴더에 올린다 (2026-08-19). */
  onDropFiles?: (folderId: string | null, folderName: string, files: File[]) => void;
  /**
   * 맨 위 루트 줄('워크스페이스')을 감춘다 (2026-09-02, 기본 false).
   *
   * 트리를 **'내 파일' 아래**로 옮기면서 필요해졌다 — 그 자리에서는 '내 파일' 항목 자체가
   * 루트라, 바로 밑에 같은 뜻의 줄을 하나 더 그리면 계층이 두 번 접힌 것처럼 읽힌다.
   * 기본값이 false 라 이 값을 안 주는 기존 사용처는 종전과 똑같이 동작한다.
   */
  hideRoot?: boolean;
}

/**
 * FolderTree (P3-FILE-FE-2) — 좌측 사이드바 폴더 트리.
 *
 * 단순화: depth-1 lazy load — root → 클릭 시 children fetch + 캐시. 재귀 expand 가능.
 * 후속: recursive CTE backend endpoint 후 단일 fetch.
 *
 * ★ 2026-08-21 — `foldersOnly` 로 **폴더만** 받는다.
 *
 *   전에는 목록 API 를 그대로 부르고 `children` 에서 폴더만 골라 썼다. 그래서
 *   (1) 파일 최대 100건의 배지·공유 페이로드를 받아 대부분 버렸고,
 *   (2) 서버는 트리 요청에도 스마트폴더·용량을 계산했으며(루트 목록 한 번치 비용),
 *   (3) 무엇보다 **폴더가 첫 페이지에 없으면 트리가 통째로 비었다** — 운영에서
 *       외부메일 첨부 156건이 들어오자 폴더가 200위로 밀려 실제로 그렇게 됐다.
 *   이제 서버가 폴더를 구획으로 따로 뽑아 주므로 셋 다 사라진다.
 */
export function FolderTree({ activeFolderId, onSelect, version = 0, onDropMove, onDropFiles, hideRoot = false, onFolderMenu, revealIds }: FolderTreeProps) {
  /** 지금 무엇 위에 떠 있는가 — 루트는 `'root'`. */
  const [dropId, setDropId] = useState<string | null>(null);
  const [rootFolders, setRootFolders] = useState<NodePublic[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [childrenCache, setChildrenCache] = useState<Record<string, NodePublic[]>>({});
  const [loading, setLoading] = useState(true);
  /** 갱신 effect 가 "지금 펼쳐진 것" 을 읽기 위한 거울 — effect 의존성을 늘리지 않는다. */
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const revealRef = useRef(revealIds);
  revealRef.current = revealIds;

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const res = await listFolderChildren('root', { foldersOnly: true });
        setRootFolders(foldersOf(res));
        /*
         * 펼쳐 둔 폴더의 하위도 **다시 읽는다**. 캐시만 비우면 펼쳐 놓은 가지가 갑자기
         * 비어 보이고, 펼침 상태까지 접으면 폴더를 하나 만들 때마다 트리가 통째로 접힌다.
         */
        const reveal = revealRef.current ?? [];
        const open = [...new Set([...expandedRef.current, ...reveal])];
        if (reveal.some((id) => !expandedRef.current.has(id))) setExpanded(new Set(open));
        if (open.length > 0) {
          const pairs = await Promise.all(open.map(async (id) => {
            try {
              const sub = await listFolderChildren(id, { foldersOnly: true });
              return [id, foldersOf(sub)] as const;
            } catch {
              return [id, [] as NodePublic[]] as const;
            }
          }));
          setChildrenCache(Object.fromEntries(pairs));
        } else {
          setChildrenCache({});
        }
      } catch {
        setRootFolders([]);
      } finally {
        setLoading(false);
      }
    })();
    // expanded 는 의도적으로 의존성에서 뺀다 — 펼칠 때마다 트리 전체를 다시 읽을 이유가 없다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  /**
   * 놓을 수 있는 곳의 공통 처리.
   *
   * ⚠ `dragover` 에서는 dataTransfer 의 **내용을 못 읽는다**(브라우저가 막는다). 그래서
   *   "우리 짐인가" 는 형식(types)으로만 보고, "자기 자신 위인가" 같은 내용 판정은
   *   drop 순간에 한다. 그 전까지는 일단 받을 수 있는 것으로 표시한다.
   */
  function dropProps(key: string, folderId: string | null, folderName: string) {
    if (!onDropMove && !onDropFiles) return {};
    /** 우리 짐(내부 이동)인가, 바깥 파일(업로드)인가 — 둘 다 아니면 손대지 않는다. */
    const kindOf = (dt: DataTransfer | null) => {
      if (onDropMove && hasWorkspaceNodes(dt)) return 'move' as const;
      if (onDropFiles && hasExternalFiles(dt)) return 'files' as const;
      return null;
    };
    return {
      onDragOver: (e: React.DragEvent) => {
        const kind = kindOf(e.dataTransfer);
        if (!kind) return;
        e.preventDefault();          // 안 하면 drop 이 아예 오지 않는다
        e.stopPropagation();          // 부모 행까지 같이 밝아지지 않게
        e.dataTransfer.dropEffect = kind === 'files' ? 'copy' : 'move';
        if (dropId !== key) setDropId(key);
      },
      onDragLeave: () => setDropId((cur) => (cur === key ? null : cur)),
      onDrop: (e: React.DragEvent) => {
        const kind = kindOf(e.dataTransfer);
        if (!kind) return;
        e.preventDefault();
        e.stopPropagation();
        setDropId(null);
        if (kind === 'files') {
          const files = readExternalFiles(e.dataTransfer);
          if (files.length > 0) onDropFiles?.(folderId, folderName, files);
          return;
        }
        const ids = readWorkspaceNodes(e.dataTransfer);
        // 자기 자신 안으로는 못 넣는다 — 트리가 그 폴더를 통째로 들고 있는 셈이 된다.
        if (ids.length === 0 || (folderId !== null && ids.includes(folderId))) return;
        onDropMove?.(ids, folderId);
      },
    };
  }

  async function toggleExpand(folder: NodePublic) {
    const isOpen = expanded.has(folder.id);
    if (isOpen) {
      setExpanded((s) => { const n = new Set(s); n.delete(folder.id); return n; });
      return;
    }
    setExpanded((s) => new Set(s).add(folder.id));
    if (!childrenCache[folder.id]) {
      try {
        const res = await listFolderChildren(folder.id, { foldersOnly: true });
        setChildrenCache((c) => ({ ...c, [folder.id]: foldersOf(res) }));
      } catch {
        setChildrenCache((c) => ({ ...c, [folder.id]: [] }));
      }
    }
  }

  return (
    <aside className="workspace-tree" aria-label={tWorkspace('workspace.tree.aria')}>
      {/*
        루트 줄('워크스페이스') — 트리를 **'내 파일' 아래**로 옮기면서 감출 수 있게 했다
        (2026-09-02). 그 자리에서는 '내 파일' 항목 자체가 루트라, 바로 밑에 같은 뜻의 줄을
        하나 더 그리면 계층이 두 번 접힌 것처럼 읽힌다.
        기본값은 종전대로 **보인다** — 다른 데서 쓰는 트리는 한 글자도 안 바뀐다.
      */}
      {!hideRoot && (
        <button
          type="button"
          className={`workspace-tree-item ${activeFolderId === null ? 'workspace-tree-item--active' : ''}`
            + (dropId === 'root' ? ' workspace-tree-item--drop' : '')}
          onClick={() => onSelect(null)}
          {...dropProps('root', null, tWorkspace('workspace.title'))}
        >
          <Folder size={14} aria-hidden />
          <span className="workspace-tree-item-name">{tWorkspace('workspace.title')}</span>
        </button>
      )}

      {loading && <div className="workspace-tree-loading">{tWorkspace('workspace.tree.loading')}</div>}
      {!loading && rootFolders.length === 0 && (
        <div className="workspace-tree-empty">{tWorkspace('workspace.tree.empty')}</div>
      )}

      <ul className="workspace-tree-list">
        {rootFolders.map((f) => (
          <FolderNode
            key={f.id}
            folder={f}
            depth={0}
            activeId={activeFolderId}
            expanded={expanded}
            childrenCache={childrenCache}
            onSelect={onSelect}
            onToggle={toggleExpand}
            dropId={dropId}
            dropProps={dropProps}
            {...(onFolderMenu ? { onMenu: onFolderMenu } : {})}
          />
        ))}
      </ul>
    </aside>
  );
}

interface FolderNodeProps {
  folder: NodePublic;
  depth: number;
  activeId: string | null;
  expanded: Set<string>;
  childrenCache: Record<string, NodePublic[]>;
  onSelect: (folder: NodePublic) => void;
  onToggle: (folder: NodePublic) => void | Promise<void>;
  /** 지금 무엇 위에 떠 있는가(트리 전체에서 하나). */
  dropId?: string | null;
  /** 놓을 수 있는 곳으로 만드는 속성 — 트리가 만들어 내려 준다. */
  dropProps?: (key: string, folderId: string | null, folderName: string) => Record<string, unknown>;
  /** 2026-10-15 — 줄 끝 「…」 메뉴. 없으면 단추를 안 그린다. */
  onMenu?: (folder: NodePublic, anchor: HTMLElement) => void;
}

function FolderNode({
  folder, depth, activeId, expanded, childrenCache, onSelect, onToggle, dropId, dropProps, onMenu,
}: FolderNodeProps) {
  const isOpen = expanded.has(folder.id);
  const cached = childrenCache[folder.id];
  const children = cached ?? [];
  /*
   * 펼침 화살표는 **하위 폴더가 있을 때만** 둔다 (2026-08-19).
   *
   * 예전에는 무조건 그렸다 — 파일만 든 폴더나 빈 폴더도 화살표가 있었고, 눌러도 아무것도
   * 안 나왔다. 한 번 펼쳐 봤으면 받아 둔 캐시가 진실이고, 아직이면 서버가 준
   * hasChildFolder 를 믿는다. 그 필드가 없는 응답(구버전 서버)이면 예전처럼 화살표를 둔다
   * — 하위 폴더가 있는데 못 펼치는 것보다 헛도는 화살표가 낫다.
   */
  const expandable = cached ? cached.length > 0 : (folder.hasChildFolder ?? true);
  const isActive = activeId === folder.id;
  return (
    <li className="workspace-tree-node" style={{ paddingLeft: depth * 12 }}>
      <div
        className={`workspace-tree-row ${isActive ? 'workspace-tree-row--active' : ''}`
          + (dropId === folder.id ? ' workspace-tree-row--drop' : '')}
        {...(dropProps?.(folder.id, folder.id, folder.name) ?? {})}
      >
        {expandable ? (
          <button
            type="button"
            className="workspace-tree-toggle"
            onClick={() => onToggle(folder)}
            aria-label={isOpen ? tWorkspace('workspace.tree.aria_collapse') : tWorkspace('workspace.tree.aria_expand')}
          >
            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          // 화살표 자리만 비운다 — 안 그러면 이름 줄이 폴더마다 어긋난다.
          <span className="workspace-tree-toggle workspace-tree-toggle--empty" aria-hidden />
        )}
        <button
          type="button"
          className="workspace-tree-item workspace-tree-item--child"
          onClick={() => onSelect(folder)}
        >
          {isOpen ? <FolderOpen size={14} aria-hidden /> : <Folder size={14} aria-hidden />}
          {/* 이름이 길면 잘린다 — 전체는 마우스를 올려 본다(2026-08-18). */}
          <span className="workspace-tree-item-name" title={folder.name}>{folder.name}</span>
        </button>
        {onMenu && (
          <button
            type="button"
            className="workspace-tree-more"
            aria-label={tWorkspace('workspace.row.aria_menu', { name: folder.name })}
            aria-haspopup="menu"
            onClick={(e) => { e.stopPropagation(); onMenu(folder, e.currentTarget); }}
          >
            {/* 본문 목록의 ⋮(`.ws-row-menu`)와 같은 모양 — 2026-10-27 사용자 지시 */}
            <MoreVertical size={16} aria-hidden />
          </button>
        )}
      </div>
      {isOpen && children.length > 0 && (
        <ul className="workspace-tree-list">
          {children.map((c) => (
            <FolderNode
              key={c.id}
              folder={c}
              depth={depth + 1}
              activeId={activeId}
              expanded={expanded}
              childrenCache={childrenCache}
              onSelect={onSelect}
              onToggle={onToggle}
              dropId={dropId}
              dropProps={dropProps}
              {...(onMenu ? { onMenu } : {})}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
